import {
  CalloutComponent,
  FormValues,
  getValue,
  InputFormCallout,
  InputFormGroup,
  IntegerInput,
  SubmitButtonMode,
} from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { CalloutTestIds } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { mdsBackupPolicyMaxRetentionInDays, mdsBackupPolicyMinRetentionInDays } from "../../helpers/resourceHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  MDS_UPDATE_POLL_DELAY,
  MDS_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export enum Fields {
  RetentionDays = "retention-days",
}

export enum FieldTestIds {
  RetentionDays = "retention-days",
}

export interface MysqlBackupEditRetentionDaysProps extends OperationActionProps {
  backupId: string | undefined;
  retentionDays: number | undefined;
}

const MysqlBackupEditRetentionDays = (
  { targetId, location, backupId, retentionDays, onExecute, onCancel }: MysqlBackupEditRetentionDaysProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(backupId);
  const resourceGroupName = decodeURIComponent(resourceGroup || "");
  const backupName = decodeURIComponent(resourceName || "");

  const [initialValues, setInitialValues] = React.useState<FormValues>();

  React.useEffect(() => {
    // eslint-disable-next-line eqeqeq
    if (retentionDays != undefined) {
      const initFieldValues: FormValues = {
        // eslint-disable-next-line eqeqeq
        [Fields.RetentionDays]: retentionDays != undefined
          ? Number(retentionDays).toString()
          : undefined,
      };
      setInitialValues(initFieldValues);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retentionDays]);

  const { invokeCall: invokeMdsUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.updateMdsBackup },
  );
  const [calloutRef, setCalloutRef] = React.useState<CalloutComponent>({} as CalloutComponent);

  const onUpdate = (formValues: FormValues): void => {
    const retention = getValue<string>(formValues, Fields.RetentionDays, InputFormGroup);
    const retentionInDays = retention ? parseInt(retention, 10) : undefined;
    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: calloutRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateMysqlBackupRetentionDays(),
          message: Messages.notifications.inProgress.messages.updateMysqlBackupRetentionDays(backupName),
        },
        success: {
          title: Messages.notifications.success.titles.updateMysqlBackupRetentionDays(),
          message: Messages.notifications.success.messages.updateMysqlBackupRetentionDays(backupName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateMysqlBackupRetentionDays(),
          message: Messages.notifications.failure.messages.updateMysqlBackupRetentionDays(backupName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.MDS_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName,
            backupKey: backupName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.MDS_UPDATED_CHECK,
          delay: MDS_UPDATE_POLL_DELAY,
          interval: MDS_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeMdsUpdate({
      backupKey: backupName,
      resourceGroupName,
      subscriptionId,
      updateMdsBackupDetails: { retentionInDays },
    }, invokeOptions);
  };

  return (
    <InputFormCallout
      targetId={targetId}
      title={Messages.createNewPanels.editRetentionDays.title()}
      componentRef={setCalloutRef}
      submitButtonMode={SubmitButtonMode.DISABLE_TILL_VALID}
      onSubmit={onUpdate}
      onClose={onCancel}
      testId={CalloutTestIds.MysqlBackupEditRetentionDays}
      initialValues={initialValues}
    >
      <IntegerInput
        testId={FieldTestIds.RetentionDays}
        fieldName={Fields.RetentionDays}
        label={Messages.labels.backupRetentionPeriod()}
        defaultValue={retentionDays}
        min={mdsBackupPolicyMinRetentionInDays}
        max={mdsBackupPolicyMaxRetentionInDays}
        required
      />
    </InputFormCallout>
  );
};

export const newMysqlBackupEditRetentionDays = (
  props: MysqlBackupEditRetentionDaysProps,
): JSX.Element => (<MysqlBackupEditRetentionDays {...props} />);
